import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  TrendingUp, Calendar, Star, IndianRupee, Users, ArrowRight, Download,
  CheckCircle2, Clock, LogIn, LogOut as LogOutIcon, MessageSquare, Send,
  Plus, Trash2, Sparkles, ImageIcon, Upload, X, Image as ImageIconLucide,
} from 'lucide-react';
import { compressManyImages } from '../../utils/imageCompress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const formatDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};
const STATUS_PILL = {
  pending: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  confirmed: 'bg-[#FFE5EE] text-[#CC0047] border-[#FF0059]/30',
  checked_in: 'bg-blue-50 text-blue-700 border-blue-200',
  checked_out: 'bg-green-50 text-green-700 border-green-200',
  cancelled: 'bg-gray-100 text-gray-500 border-gray-200',
  no_show: 'bg-red-50 text-red-700 border-red-200',
};

const SectionTitle = ({ title, subtitle, action }) => (
  <div className="flex items-start justify-between gap-4 mb-6">
    <div>
      <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-black">{title}</h2>
      {subtitle && <p className="text-sm text-gray-600 mt-1">{subtitle}</p>}
    </div>
    {action}
  </div>
);

// ========================================================================
// HOME
// ========================================================================
export const HomeSection = ({ bookings, onNav, insights }) => {
  const today = new Date().toISOString().slice(0, 10);
  const arrivalsToday = bookings.filter((b) => b.check_in === today && b.status === 'confirmed');
  const departuresToday = bookings.filter((b) => b.check_out === today && b.status !== 'checked_out');
  const totals = insights?.totals || {};

  const Tile = ({ icon: Icon, label, value, color = '#FF0059' }) => (
    <div className="bg-white border border-gray-200 rounded-2xl p-5">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${color}15` }}>
          <Icon className="h-4 w-4" style={{ color }} />
        </div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">{label}</p>
      </div>
      <p className="text-2xl sm:text-3xl font-black text-black">{value}</p>
    </div>
  );

  return (
    <div data-testid="hotel-home-section">
      <SectionTitle title="Welcome back 👋" subtitle="A snapshot of what's happening at your property today." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Tile icon={Calendar} label="Arrivals today" value={arrivalsToday.length} />
        <Tile icon={LogOutIcon} label="Departures today" value={departuresToday.length} color="#FFCB00" />
        <Tile icon={TrendingUp} label="Bookings · 30d" value={totals.bookings_30d ?? 0} />
        <Tile icon={IndianRupee} label="Revenue · 30d" value={formatINR(totals.revenue_30d ?? 0)} color="#0F9D58" />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="arrivals-today-card">
          <h3 className="text-lg font-black text-black mb-3 flex items-center gap-2"><LogIn className="h-4 w-4 text-[#FF0059]" /> Today's arrivals</h3>
          {arrivalsToday.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">No arrivals today.</p>
          ) : (
            <ul className="divide-y divide-gray-100 -my-2">
              {arrivalsToday.slice(0, 5).map((b) => (
                <li key={b._id} className="py-2.5 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-bold text-sm text-black truncate">{b.guest_name}</p>
                    <p className="text-xs text-gray-500">{b.guests} guest{b.guests > 1 ? 's' : ''} · {b.nights} night{b.nights > 1 ? 's' : ''}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${STATUS_PILL[b.status] || ''}`}>{b.status}</span>
                </li>
              ))}
            </ul>
          )}
          <button onClick={() => onNav('reservations')} className="mt-3 text-xs font-bold text-[#FF0059] hover:underline inline-flex items-center gap-1">
            See all reservations <ArrowRight className="h-3 w-3" />
          </button>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="rating-snapshot-card">
          <h3 className="text-lg font-black text-black mb-3 flex items-center gap-2"><Star className="h-4 w-4 text-[#FFCB00]" /> Rating snapshot</h3>
          <div className="flex items-baseline gap-3 mb-3">
            <p className="text-5xl font-black text-black">{(totals.avg_rating ?? 0).toFixed(1)}</p>
            <p className="text-sm text-gray-500">/ 10 · {totals.review_count ?? 0} reviews</p>
          </div>
          {[10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map((s) => {
            const count = insights?.rating_distribution?.[s] || insights?.rating_distribution?.[String(s)] || 0;
            const max = Math.max(1, ...Object.values(insights?.rating_distribution || {}));
            const pct = (count / max) * 100;
            return (
              <div key={s} className="flex items-center gap-2 text-xs mb-1">
                <span className="w-5 text-right font-black tabular-nums">{s}</span>
                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#FFCB00]" style={{ width: `${pct}%` }} />
                </div>
                <span className="w-8 text-right text-gray-500">{count}</span>
              </div>
            );
          })}
          <button onClick={() => onNav('reviews')} className="mt-3 text-xs font-bold text-[#FF0059] hover:underline inline-flex items-center gap-1">
            Read reviews <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ========================================================================
// RESERVATIONS
// ========================================================================
export const ReservationsSection = ({ bookings, reloadBookings }) => {
  const [filter, setFilter] = useState('upcoming');
  const [query, setQuery] = useState('');

  const today = new Date().toISOString().slice(0, 10);
  const filtered = bookings.filter((b) => {
    const matchesText = !query || b.guest_name?.toLowerCase().includes(query.toLowerCase()) || (b.booking_id || '').toLowerCase().includes(query.toLowerCase());
    if (!matchesText) return false;
    if (filter === 'today') return b.check_in === today || b.check_out === today;
    if (filter === 'upcoming') return b.check_in >= today && b.status !== 'cancelled';
    if (filter === 'past') return b.check_out < today;
    if (filter === 'cancelled') return b.status === 'cancelled';
    return true;
  });

  const checkInBooking = async (b) => {
    try {
      await axios.put(`${API}/hotel/bookings/${b._id}/check-in`, {}, { withCredentials: true });
      toast.success('Guest checked in');
      reloadBookings();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const checkOutBooking = async (b) => {
    try {
      const { data } = await axios.put(`${API}/hotel/bookings/${b._id}/check-out`, {}, { withCredentials: true });
      toast.success(`Checked out. ₹${data.wallet_credited} credited to guest wallet.`);
      reloadBookings();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="reservations-section">
      <SectionTitle title="Reservations" subtitle={`${filtered.length} reservation${filtered.length === 1 ? '' : 's'}`} />
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {[
          { id: 'upcoming', label: 'Upcoming' },
          { id: 'today', label: 'Today' },
          { id: 'past', label: 'Past' },
          { id: 'cancelled', label: 'Cancelled' },
          { id: 'all', label: 'All' },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${filter === f.id ? 'bg-[#FF0059] text-white' : 'bg-white border border-gray-200 text-gray-700 hover:border-[#FF0059]'}`}
            data-testid={`reservations-filter-${f.id}`}
          >{f.label}</button>
        ))}
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by guest or booking ID…"
          className="flex-1 sm:flex-none sm:w-72 ml-auto px-3 py-1.5 rounded-full border border-gray-200 focus:border-[#FF0059] focus:ring-2 focus:ring-[#FFE5EE] outline-none text-sm font-medium"
          data-testid="reservations-search-input"
        />
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {filtered.length === 0 ? (
          <p className="text-center text-sm text-gray-500 py-12">No reservations match your filter.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {filtered.map((b) => (
              <li key={b._id} className="p-4 flex flex-col lg:flex-row lg:items-center gap-3" data-testid={`reservation-row-${b._id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold text-black truncate">{b.guest_name}</p>
                    <span className="text-xs font-mono text-gray-400">#{b.booking_id || b._id.slice(-8).toUpperCase()}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${STATUS_PILL[b.status] || ''}`}>{b.status}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    <Calendar className="h-3 w-3 inline mr-1" />
                    {formatDate(b.check_in)} → {formatDate(b.check_out)} · {b.nights} night{b.nights > 1 ? 's' : ''} · {b.guests} guest{b.guests > 1 ? 's' : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-black text-black inline-flex items-center"><IndianRupee className="h-3.5 w-3.5" />{Number(b.amount || b.total_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                  {b.status === 'confirmed' && (
                    <button onClick={() => checkInBooking(b)} className="px-3 py-1.5 rounded-full bg-[#FF0059] hover:bg-[#CC0047] text-white text-xs font-bold" data-testid={`check-in-${b._id}`}>
                      Check-in
                    </button>
                  )}
                  {(b.status === 'checked_in' || b.status === 'confirmed') && (
                    <button onClick={() => checkOutBooking(b)} className="px-3 py-1.5 rounded-full bg-black hover:bg-gray-800 text-white text-xs font-bold" data-testid={`check-out-${b._id}`}>
                      Check-out
                    </button>
                  )}
                  <a
                    href={`${API}/hotel/bookings/${b._id}/voucher`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-gray-200 hover:border-[#FF0059] text-xs font-bold text-black"
                    data-testid={`voucher-${b._id}`}
                  >
                    <Download className="h-3 w-3" /> Voucher
                  </a>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
